import React, { useState } from "react";
import { SkipBackIcon as Backspace, Lock, RotateCcw } from "lucide-react";
import { z } from "zod/v3";
import { useStore } from "@tanstack/react-form";

import { Button } from "@workspace/ui/components/button";
import { Input } from "@workspace/ui/components/input";
import {
	revalidateLogic,
	useAppForm,
} from "@workspace/ui/components/tanstack-form";
import { cn } from "@workspace/ui/lib/utils";

// Common easily guessable PINs
const COMMON_PINS = new Set([
	"0000",
	"1111",
	"2222",
	"3333",
	"4444",
	"5555",
	"6666",
	"7777",
	"8888",
	"9999",
	"1234",
	"4321",
	"1111",
	"0123",
	"2580",
	"1357",
	"2468",
	"9876",
	"6543",
	"5432",
	"1122",
	"3344",
	"5566",
	"7788",
	"9900",
	"1212",
	"2323",
	"3434",
	"4545",
	"5656",
	"6767",
	"7878",
	"8989",
	"9090",
	"0101",
	"0202",
	"0303",
	"0404",
	"0505",
	"0606",
	"0707",
	"0808",
	"0909",
	"1010",
	"1313",
	"1414",
	"1515",
	"1616",
	"1717",
	"1818",
	"1919",
	"2020",
	"2121",
	"2424",
	"2525",
	"2626",
	"2727",
	"2828",
	"2929",
	"3030",
]);

const isSequentialOrRepeating = (pin: string): boolean => {
	// Check for all same digits
	if (new Set(pin).size === 1) return true;

	// Check for sequential ascending (1234, 2345, etc.)
	let isAscending = true;
	for (let i = 1; i < pin.length; i++) {
		const currentDigit = parseInt(pin.charAt(i));
		const prevDigit = parseInt(pin.charAt(i - 1));
		const diff = currentDigit - prevDigit;
		if (diff !== 1 && !(prevDigit === 9 && currentDigit === 0)) {
			isAscending = false;
			break;
		}
	}

	// Check for sequential descending (4321, 5432, etc.)
	let isDescending = true;
	for (let i = 1; i < pin.length; i++) {
		const currentDigit = parseInt(pin.charAt(i));
		const prevDigit = parseInt(pin.charAt(i - 1));
		const diff = prevDigit - currentDigit;
		if (diff !== 1 && !(currentDigit === 9 && prevDigit === 0)) {
			isDescending = false;
			break;
		}
	}

	return isAscending || isDescending;
};

const PinResetSchema = z
	.object({
		newPin: z
			.string()
			.length(4, "PIN must be exactly 4 digits")
			.refine((pin) => /^\d{4}$/.test(pin), "PIN must contain only digits")
			.refine(
				(pin) => !COMMON_PINS.has(pin),
				"This PIN is too common. Please choose a different one.",
			)
			.refine(
				(pin) => !isSequentialOrRepeating(pin),
				"Avoid sequential or repeating patterns for better security.",
			),
		confirmPin: z.string().length(4, "PIN must be exactly 4 digits"),
	})
	.refine((data) => data.newPin === data.confirmPin, {
		message: "PINs do not match",
		path: ["confirmPin"],
	});

export type PinResetData = z.infer<typeof PinResetSchema>;

export function StaffPinResetForm({
	onSubmit,
	onCancel,
}: {
	onSubmit: (data: PinResetData) => Promise<boolean>;
	onCancel?: () => void;
}) {
	const [activeField, setActiveField] = useState<
		"newPin" | "confirmPin" | null
	>("newPin");

	const form = useAppForm({
		defaultValues: {
			newPin: "",
			confirmPin: "",
		} as PinResetData,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: {
			onDynamic: PinResetSchema,
		},
		onSubmit: async ({ value, ...rest }) => {
			const result = await onSubmit(value);

			if (result) {
				rest.formApi.reset();
				setActiveField("newPin");
			} else {
				// Stay on current form state on error
			}
		},
	});

	const handleNumberClick = async (number: string) => {
		if (activeField === "newPin") {
			form.setFieldValue("newPin", (prev) =>
				prev.length < 4 ? prev + number : prev,
			);
		} else if (activeField === "confirmPin") {
			form.setFieldValue("confirmPin", (prev) =>
				prev.length < 4 ? prev + number : prev,
			);
		}

		if (form.state.submissionAttempts > 0) await form.validate("change");
	};

	const handleBackspace = () => {
		if (activeField === "newPin") {
			form.setFieldValue("newPin", (prev) => prev.slice(0, -1));
		} else if (activeField === "confirmPin") {
			form.setFieldValue("confirmPin", (prev) => {
				if (prev.length === 0) {
					setActiveField("newPin");
				}

				const n = prev.slice(0, -1);

				return n;
			});
		}
	};

	const handleClear = () => {
		if (activeField === "newPin") {
			form.setFieldValue("newPin", "");
		} else if (activeField === "confirmPin") {
			form.setFieldValue("confirmPin", "");
		}
	};

	const handleNext = () => {
		if (activeField === "newPin") {
			setActiveField("confirmPin");
		}
	};

	const getButtonState = () => {
		if (activeField === "newPin") {
			return { text: "Next", action: "next", disabled: false };
		}

		if (activeField === "confirmPin") {
			return {
				text: "Reset PIN",
				action: "submit",
			};
		}

		return { text: "Reset PIN", action: "submit", disabled: true };
	};

	const numbers = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"];
	const buttonState = getButtonState();

	const validationState = useStore(form.store, (state) => state.errorMap);

	React.useEffect(() => {
		if (validationState.onDynamic?.newPin) {
			// eslint-disable-next-line react-hooks/set-state-in-effect -- HA-738: react-hooks 7.1 finding, refactor deferred
			setActiveField("newPin");
		}
	}, [validationState]);

	return (
		<>
			<div className="space-y-4">
				<form
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
					className="space-y-4"
				>
					<div className="text-center space-y-2">
						<div className="flex items-center justify-center gap-2 text-lg font-semibold">
							<RotateCcw className="size-5" />
							Reset Your PIN
						</div>
						<p className="text-sm text-muted-foreground">
							Enter a new 4-digit PIN and confirm it
						</p>
						<p className="text-xs text-muted-foreground">
							Avoid common patterns like 1234, 0000, or repeating digits for
							better security
						</p>
					</div>
					<div className="space-y-4 grid grid-cols-2 gap-2 items-start">
						<form.AppField name="newPin">
							{(field) => (
								<field.FormItem>
									<field.FormLabel className="flex items-center gap-2">
										<Lock className="size-4" />
										New PIN
									</field.FormLabel>
									<field.FormControl>
										<Input
											id="newPin"
											name="new-pin-input"
											type="password"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={() => field.handleBlur()}
											onFocus={() => setActiveField("newPin")}
											onMouseDown={(e) => e.preventDefault()}
											onClick={(e) => {
												e.preventDefault();
												setActiveField("newPin");
											}}
											placeholder="••••"
											maxLength={4}
											autoComplete="new-password"
											autoCorrect="off"
											autoCapitalize="off"
											spellCheck="false"
											data-lpignore="true"
											data-1p-ignore="true"
											data-bwignore="true"
											data-form-type="other"
											className={cn(
												"text-center text-lg font-mono tracking-widest",
												{
													"ring-2 ring-primary": activeField === "newPin",
												},
											)}
											readOnly
										/>
									</field.FormControl>
									<field.FormMessage />
									<div className="text-xs text-muted-foreground text-center">
										{field.state.value.length}/4 digits
									</div>
								</field.FormItem>
							)}
						</form.AppField>

						<form.AppField name="confirmPin">
							{(field) => (
								<field.FormItem>
									<field.FormLabel className="flex items-center gap-2">
										<Lock className="size-4" />
										Confirm PIN
									</field.FormLabel>
									<field.FormControl>
										<Input
											id="confirmPin"
											name="confirm-pin-input"
											type="password"
											value={field.state.value}
											onChange={(e) => field.handleChange(e.target.value)}
											onBlur={() => field.handleBlur()}
											onFocus={() => setActiveField("confirmPin")}
											onMouseDown={(e) => e.preventDefault()}
											onClick={(e) => {
												e.preventDefault();
												setActiveField("confirmPin");
											}}
											placeholder="••••"
											maxLength={4}
											autoComplete="new-password"
											autoCorrect="off"
											autoCapitalize="off"
											spellCheck="false"
											data-lpignore="true"
											data-1p-ignore="true"
											data-bwignore="true"
											data-form-type="other"
											className={cn(
												"text-center text-lg font-mono tracking-widest",
												{
													"ring-2 ring-primary": activeField === "confirmPin",
												},
											)}
											readOnly
										/>
									</field.FormControl>
									<field.FormMessage />
									<div className="text-xs text-muted-foreground text-center">
										{field.state.value.length}/4 digits
									</div>
								</field.FormItem>
							)}
						</form.AppField>
					</div>

					{/* Number Pad */}
					<div className="grid gap-3">
						<div className="grid grid-cols-3 gap-3">
							{numbers.slice(0, 9).map((number) => (
								<Button
									key={number}
									type="button"
									variant="outline"
									size="lg"
									onClick={() => handleNumberClick(number)}
									disabled={!activeField}
									className="h-12 text-lg font-semibold"
								>
									{number}
								</Button>
							))}
						</div>

						<div className="grid grid-cols-3 gap-3">
							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={handleClear}
								disabled={!activeField}
								className="h-12 text-sm bg-transparent"
							>
								Clear
							</Button>

							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={() => handleNumberClick("0")}
								disabled={!activeField}
								className="h-12 text-lg font-semibold"
							>
								0
							</Button>

							<Button
								type="button"
								variant="outline"
								size="lg"
								onClick={handleBackspace}
								disabled={!activeField}
								className="h-12 bg-transparent"
							>
								<Backspace className="h-5 w-5" />
							</Button>
						</div>
					</div>

					{/* Action Buttons */}
					<div className="space-y-3">
						{buttonState.action === "submit" && (
							<form.Subscribe
								selector={(state) => [
									state.values.newPin.length === 4 &&
										state.values.confirmPin.length === 4,
								]}
							>
								{([canSubmit]) => (
									<Button
										type="submit"
										className="w-full h-12 text-lg"
										disabled={buttonState.disabled || !canSubmit}
									>
										Reset PIN
									</Button>
								)}
							</form.Subscribe>
						)}

						{buttonState.action === "next" && (
							<Button
								type="button"
								onClick={handleNext}
								className="w-full h-12 text-lg"
								disabled={buttonState.disabled}
							>
								Next
							</Button>
						)}

						{onCancel && (
							<Button
								type="button"
								variant="outline"
								onClick={onCancel}
								className="w-full h-12 text-lg"
							>
								Cancel
							</Button>
						)}
					</div>
				</form>
			</div>
		</>
	);
}
